import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const ToolsTemplates = () => {
  const [activeFolder, setActiveFolder] = useState(null);

  const folders = [
    { name: 'Brand Assets', items: ['Logos', 'Brand Guidelines', 'Color Palettes', 'Fonts'] },
    { name: 'Content Calendar', items: ['2024', '2025', 'Templates'] },
    { name: 'Creative Assets', items: ['Photography', 'Graphics', 'Videos', 'Templates'] },
    { name: 'Performance Reports', items: ['Monthly Reports', 'Weekly Metrics', 'Campaign Analytics'] },
  ];

  const frameworks = [
    { title: 'Educational Post', content: 'Hook + Context + 3 Key Points + CTA' },
    { title: 'Value/Insight', content: 'Hook + Personal Story + Insight + Application' },
    { title: 'Promotional', content: 'Problem + Agitation + Solution + Proof + CTA' },
    { title: 'UGC Feature', content: 'Appreciation + Context + Value + Question' }
  ];

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }}
      className="max-w-7xl mx-auto"
    >
      <h1 className="section-title">Tools & Assets</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Interactive File System */}
        <div className="section-card h-full">
          <h2 className="subsection-title">File Organization System</h2>
          <div className="bg-beige/50 rounded-xl p-4 border border-gold/20 min-h-[300px]">
            {folders.map((folder, i) => (
              <div key={i} className="mb-2">
                <button 
                  onClick={() => setActiveFolder(activeFolder === i ? null : i)}
                  className="flex items-center w-full text-left p-2 hover:bg-white/50 rounded-lg transition-colors"
                >
                  <SafeIcon 
                    name={activeFolder === i ? 'FolderMinus' : 'FolderPlus'} 
                    className={`mr-3 ${activeFolder === i ? 'text-gold' : 'text-brown/40'}`} 
                  />
                  <span className="font-medium text-brown">{folder.name}</span>
                </button>
                <AnimatePresence>
                  {activeFolder === i && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden pl-11"
                    >
                      {folder.items.map((item, j) => (
                        <div key={j} className="py-1.5 text-sm text-brown/60 flex items-center">
                           <div className="w-1.5 h-1.5 bg-gold/50 rounded-full mr-2"></div>
                           {item}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>

        {/* Framework Cards */}
        <div className="section-card h-full">
          <h2 className="subsection-title">Caption Frameworks</h2>
          <div className="space-y-4">
            {frameworks.map((fw, i) => (
              <motion.div 
                key={i}
                className="bg-beige p-4 rounded-lg border border-gold/20"
                whileHover={{ scale: 1.02 }}
              >
                <h3 className="text-brown font-bold mb-1">{fw.title}</h3>
                <p className="text-brown/70 text-sm font-mono">{fw.content}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Asset Briefs */}
      <div className="section-card mt-6">
        <h2 className="subsection-title">Creative Brief Template</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {['Objective', 'Target Audience', 'Key Message', 'Tone/Style', 'Visuals', 'Copy Specs', 'Deliverables', 'Approval'].map((field, i) => (
            <div key={i} className="border border-gold/20 p-3 rounded-lg bg-beige/30 text-center">
              <span className="text-xs font-bold text-gold uppercase block mb-1">Field {i+1}</span>
              <span className="font-medium text-brown">{field}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default ToolsTemplates;